import {Anchor, Group, Stack, Text, createStyles} from '@mantine/core';
import React from 'react';

import type {ScientificModelNode} from 'model-hub/scientific-models/ScientificModelMetadata';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface ModelHubModelIdentityProps {
  className?: string;
  descriptionClassName?: string;
  labels?: React.ReactNode;
  model: ScientificModelNode;
  showDescription?: boolean;
}

/** Shared model identity used across Model Hub listings. */
export default function ModelHubModelIdentity({
  className,
  descriptionClassName,
  labels,
  model,
  showDescription = true,
}: ModelHubModelIdentityProps): JSX.Element {
  const {classes, cx} = useStyles();

  return (
    <Stack className={cx(classes.root, className)} spacing={12}>
      <Group align="center" noWrap={true} spacing="sm">
        <div className={classes.iconBox}>
          <img alt={model.displayName} className={classes.icon} src={model.iconPath} />
        </div>
        <Stack spacing={4}>
          <Text fw={700} size="lg">
            {model.displayName}
          </Text>
          {model.paperLink ? (
            <Anchor
              className={classes.referenceLink}
              href={model.paperLink}
              onClick={(event) => {
                event.stopPropagation();
              }}
              rel="noopener noreferrer"
              size="sm"
              target="_blank"
            >
              <Group noWrap={true} spacing={4}>
                <Text inherit={true}>Reference</Text>
                <BenchlingIcon className={classes.referenceIcon} icon="arrow-up-right" />
              </Group>
            </Anchor>
          ) : null}
        </Stack>
      </Group>

      {labels}

      {showDescription ? (
        <Text className={descriptionClassName} color="dimmed" size="sm">
          {model.description}
        </Text>
      ) : null}
    </Stack>
  );
}

const useStyles = createStyles((theme) => ({
  root: {
    minWidth: 0,
  },
  icon: {
    maxHeight: '80%',
    maxWidth: '80%',
    objectFit: 'contain',
  },
  iconBox: {
    alignItems: 'center',
    backgroundColor: theme.colors.blue[0],
    borderRadius: theme.radius.md,
    display: 'flex',
    flex: '0 0 48px',
    height: 48,
    justifyContent: 'center',
    width: 48,
  },
  referenceIcon: {
    fontSize: theme.fontSizes.xs,
  },
  referenceLink: {
    color: theme.colors.gray[6],
    textDecoration: 'none',

    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));
